<template>
  <div class="become-author">
    <div class="become-author__container">
      <h1 class="become-author__title">
        {{ _T("@Blog become an author title") }}
      </h1>
      <div
        class="become-author__conditions ui-rich-text"
        v-html="_T('@Blog become an author conditions')"
      />
      <MyForm
        id="subscription"
        name="subscription"
        :schema="schema"
        :state="form"
        class="become-author__form"
        @submit="onSubmit"
      >
        <div class="become-author__form-container">
          <div class="become-author__form-block">
            <MyInput
              v-model.trim="form.name"
              name="name"
              autocomplete="given-name"
              caption="@Name"
              class="mobile-wide"
            />
            <MyInput
              v-model.trim="form.email"
              name="email"
              type="email"
              autocomplete="email"
              caption="E-mail"
              class="mobile-wide"
            />
          </div>
          <MyTextarea
            v-model="form.comment"
            name="comment"
            caption="@Your comment"
            :required="false"
            class="wide"
          />
          <MyCheckbox
            v-model="form.isAgreementAccepted"
            name="isAgreementAccepted"
          >
            <span>
              {{ _T("@I agree with") }}
              <MyLink name="privacy-policy" target="_blank">
                {{ _T("@privacy policy") }}
              </MyLink>
            </span>
          </MyCheckbox>
          <ButtonPrimary
            type="submit"
            :aria-label="_T('@Send')"
            button-width="262px"
            :disabled="isHandled"
          >
            {{ _T("@Send") }}
          </ButtonPrimary>
        </div>
      </MyForm>
    </div>
  </div>
</template>

<script setup>
import ButtonPrimary from "~/modules/shared/buttons/ButtonPrimary.vue";
import { useUserStore } from "~/store/userStore";
import { useSingletonHandler } from "~/uses/useSingletonHandler";
import { useAPI } from "~/uses/useMyFetch";
import {
  successfulModal,
  useModalStore,
} from "~/modules/shared/modals/store/modalStore";
import MyForm from "~/nuxt_modules/common/ui/components/form/MyForm.vue";
import MyInput from "~/nuxt_modules/common/ui/components/form/MyInput.vue";
import MyCheckbox from "~/nuxt_modules/common/ui/components/form/MyCheckbox.vue";
import MyTextarea from "~/nuxt_modules/common/ui/components/form/MyTextarea.vue";
import { z } from "zod";

const modalStore = useModalStore();
const userStore = useUserStore();
const { currentUser: user, userFullName } = storeToRefs(userStore);

onMounted(() => {
  if (null !== user.value) {
    form.name = userFullName.value;
    form.email = user.value.email;
  }
});

const successfulData = {
  title: _T("@Successful modal title"),
  description: _T("@Successful modal text"),
  isWithTimeout: true,
};

const schema = z.object({
  name: z
    .string(_T("@Entry field required"))
    .max(255, `${_T("@Maximum field length")} 255`),
  email: z
    .email(_T("@You entered an incorrect email"))
    .max(254, `${_T("@Maximum field length")} 254`),
  comment: z
    .string()
    .max(10240, `${_T("@Maximum field length")} 10240`)
    .optional(),
  isAgreementAccepted: z.literal(
    true,
    _T("@You must accept the terms and conditions"),
  ),
});

const form = reactive({
  name: undefined,
  email: undefined,
  comment: undefined,
  isAgreementAccepted: true,
});

const { handle: onSubmit, isHandled } = useSingletonHandler(
  async (data) =>
    await useAPI("/marketing/event", {
      method: "POST",
      body: {
        event: "becomeBlogAuthor",
        name: data.name,
        email: data.email,
        isConfirmed: data.isAgreementAccepted,
        form: {
          comment: data.comment,
        },
      },
    }).then(() => {
      form.name = undefined;
      form.email = undefined;
      form.comment = undefined;

      modalStore.toggleModal(successfulModal, successfulData);
    }),
);
</script>

<style scoped lang="scss">
.become-author {
  @extend %padding-wrp;

  &__container {
    @extend %width-main;

    @include flex-container(column, flex-start, center);
    gap: 24px;

    background-color: white;
    border-radius: 16px;

    padding: 16px 24px;
    margin-inline: auto;

    @include mobile {
      gap: 16px;

      padding-inline: 8px;
    }
  }

  &__title {
    max-width: 850px;
    width: 100%;

    @include font(34, 40, 600);
    text-align: center;

    @include mobile {
      @include font(22, 30, 600);
    }
  }

  &__conditions {
    max-width: 1200px;
    width: 100%;
  }

  &__form {
    max-width: 850px;
    width: 100%;

    background-color: var(--color-sky-lightest);
    border-radius: 30px;

    padding: 24px 16px;
  }

  &__form-container {
    max-width: 690px;
    width: 100%;

    @include flex-container(column, flex-start, center);
    gap: 24px;

    margin-inline: auto;

    @include mobile {
      gap: 16px;
    }
  }

  &__form-block {
    width: 100%;

    @include flex-container(row, space-between, center);
    gap: 16px;

    @include mobile {
      flex-direction: column;
    }
  }
}
</style>
